import {ref} from "vue";

// Synthesizes short feedback tones with the Web Audio API so there are no audio
// assets to ship. The AudioContext is created lazily on first play (which always
// follows a user gesture - a letter click - so autoplay policies are satisfied).
let audioCtx = null;
const muted = ref(localStorage.getItem('muted') === 'true');

export const useSound = () => {

    function getContext() {
        if (!audioCtx) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return null;
            audioCtx = new Ctx();
        }
        if (audioCtx.state === 'suspended') {
            audioCtx.resume();
        }
        return audioCtx;
    }

    // play a single tone: frequency (Hz), start offset (s), duration (s), peak gain
    function tone(freq, start, duration, peak = 0.18, type = 'sine') {
        const ctx = getContext();
        if (!ctx) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        const t = ctx.currentTime + start;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(peak, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + duration);
    }

    // soft ascending two-note "yep" (E5 -> A5)
    function playCorrect() {
        if (muted.value) return;
        tone(659.25, 0, 0.16, 0.12, 'sine');     // E5
        tone(880.0, 0.1, 0.24, 0.12, 'sine');    // A5
    }

    // soft descending two-note "nope" (A3 -> F3)
    function playWrong() {
        if (muted.value) return;
        tone(220, 0, 0.16, 0.12, 'sine');       // A3
        tone(174.61, 0.1, 0.24, 0.12, 'sine');  // F3
    }

    // bright ascending arpeggio for nailing a whole word: bigger and shinier than the
    // per-letter "yep" (reveals a word + banks a heart), but shorter than the finish run.
    function playWordCorrect() {
        if (muted.value) return;
        tone(659.25, 0, 0.12, 0.13, 'triangle');   // E5
        tone(830.61, 0.09, 0.12, 0.13, 'triangle'); // G#5
        tone(1046.5, 0.18, 0.24, 0.14, 'triangle'); // C6 (resolve)
    }

    // heavy, gritty drop for a wrong word commit: deliberately weightier than the soft
    // single-letter miss so the doubled (-2) heart cost is felt, not just seen.
    function playWordWrong() {
        if (muted.value) return;
        tone(196.0, 0, 0.18, 0.14, 'sawtooth');     // G3
        tone(146.83, 0.13, 0.36, 0.14, 'sawtooth'); // D3 (held)
    }

    // cheerful little flourish for finishing a puzzle
    function playFinish() {
        if (muted.value) return;
        tone(523.25, 0, 0.14, 0.12, 'sine');     // C5
        tone(659.25, 0.12, 0.14, 0.12, 'sine');  // E5
        tone(783.99, 0.24, 0.14, 0.12, 'sine');  // G5
        tone(1046.5, 0.36, 0.16, 0.12, 'sine');  // C6
        tone(987.77, 0.52, 0.14, 0.11, 'sine');  // B5
        tone(1046.5, 0.64, 0.4, 0.13, 'sine');   // C6 (held)
    }

    // gentle descending "aw" run for losing the game
    function playGameOver() {
        if (muted.value) return;
        tone(659.25, 0, 0.16, 0.12, 'sine');     // E5
        tone(587.33, 0.18, 0.16, 0.12, 'sine');  // D5
        tone(523.25, 0.36, 0.16, 0.12, 'sine');  // C5
        tone(440.0, 0.54, 0.16, 0.12, 'sine');   // A4
        tone(392.0, 0.72, 0.5, 0.13, 'sine');    // G4 (held)
    }

    // shimmering rise for unlocking an achievement: brighter and sparklier than the finish
    // flourish so it reads as a special reward on top of whatever just happened.
    function playAchievement() {
        if (muted.value) return;
        tone(783.99, 0, 0.12, 0.11, 'triangle');    // G5
        tone(1046.5, 0.1, 0.12, 0.12, 'triangle');  // C6
        tone(1318.51, 0.2, 0.14, 0.12, 'triangle'); // E6
        tone(1567.98, 0.3, 0.3, 0.13, 'triangle');  // G6 (held)
        tone(2093.0, 0.42, 0.25, 0.08, 'sine');     // C7 sparkle on top
    }

    function toggleMute() {
        muted.value = !muted.value;
        localStorage.setItem('muted', String(muted.value));
    }

    return {
        muted,
        toggleMute,
        playCorrect,
        playWrong,
        playWordCorrect,
        playWordWrong,
        playFinish,
        playGameOver,
        playAchievement,
    };
};
